import { useState, useEffect } from "react";
import { gameAPI } from "../utils/api";
import { Link } from "react-router-dom";
import {
  Card,
  Container,
  Row,
  Col,
  Spinner,
  Alert,
  Form,
  Badge,
  Button,
  Modal,
  Toast,
  ToastContainer,
  ProgressBar, // Added ProgressBar
} from "react-bootstrap";
import styles from "./GameList.module.css";
import { useTheme } from "../context/ThemeContext";
import { getAccessToken, getUser } from "../../utils/authStorage";
import { Gamepad2, Search } from "lucide-react"; // Added new icons

const API_BASE_URL =
  import.meta.env.VITE_API_URL || "http://localhost:3001/api/v1";

const GameList = () => {
  const { theme, toggleTheme } = useTheme(); // Added toggleTheme

  // ─── GAMIFICATION STATE ───
  const [userXP, setUserXP] = useState(0);
  const [userTier, setUserTier] = useState("NONE");
  const [userAchievements, setUserAchievements] = useState([
    { id: 1, name: "First Purchase", icon: "🎮", unlocked: false },
    { id: 2, name: "Voucher Master", icon: "💰", unlocked: false },
    { id: 3, name: "Collection Hunter", icon: "🏆", unlocked: false },
    { id: 4, name: "Speed Runner", icon: "⚡", unlocked: false },
  ]);

  // ─── VOUCHERS STATE ───
  const [vouchers, setVouchers] = useState([]);
  const [loadingVouchers, setLoadingVouchers] = useState(false);
  const [voucherError, setVoucherError] = useState("");
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [voucherFilters, setVoucherFilters] = useState({
    type: "",
    status: "active",
  });

  // ─── PURCHASE STATE ───
  const [purchasingVoucherId, setPurchasingVoucherId] = useState(null);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // ─── SHOW ACHIEVEMENTS ANIMATION ───
  const [showAchievementPopup, setShowAchievementPopup] = useState(false); // Added state for achievement popup

  const token = getAccessToken();
  const user = getUser() || {};

  // ─── STATE VARIABLES ───
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [gameAccounts, setGameAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [errorAccounts, setErrorAccounts] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [sortBy, setSortBy] = useState("popular");
  const [categories, setCategories] = useState([]);

  // ─── FETCH GAMES ───
  useEffect(() => {
    const fetchGames = async () => {
      try {
        setLoading(true);
        const params = searchTerm ? { search: searchTerm } : {};
        if (selectedCategory) params.category = selectedCategory;
        const data = await gameAPI.getGames(params);
        setGames(data.games || []);
      } catch (err) {
        setError("Failed to load games. Please try again later.");
        console.error("Error fetching games:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchGames();
  }, [searchTerm, selectedCategory]);

  // ─── FETCH GAME ACCOUNTS ───
  useEffect(() => {
    const fetchGameAccounts = async () => {
      try {
        setLoadingAccounts(true);
        const response = await fetch(`${API_BASE_URL}/game-accounts`);
        if (!response.ok) throw new Error("Failed to fetch accounts");
        const data = await response.json();
        setGameAccounts(data.accounts || []);

        const uniqueCategories = [
          ...new Set(data.accounts?.map((acc) => acc.category) || []),
        ];
        setCategories(uniqueCategories);
      } catch (err) {
        setErrorAccounts("Failed to load game accounts.");
        console.error("Error fetching game accounts:", err);
      } finally {
        setLoadingAccounts(false);
      }
    };

    fetchGameAccounts();
  }, []);

  // ─── FETCH AVAILABLE VOUCHERS ───
  useEffect(() => {
    const fetchVouchers = async () => {
      if (!token) {
        console.warn("No token available for fetching vouchers");
        return;
      }

      try {
        setLoadingVouchers(true);
        setVoucherError("");

        const queryParams = new URLSearchParams();
        if (voucherFilters.type)
          queryParams.append("type", voucherFilters.type);
        if (voucherFilters.status)
          queryParams.append("status", voucherFilters.status);

        const response = await fetch(
          `${API_BASE_URL}/vouchers/available${queryParams.toString() ? "?" + queryParams.toString() : ""}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error("Unauthorized. Please log in again.");
          }
          throw new Error("Failed to fetch vouchers");
        }

        const data = await response.json();
        setVouchers(data.vouchers || data.data || []);
        console.log(
          "✅ Vouchers fetched successfully:",
          data.vouchers?.length || 0,
        );
      } catch (err) {
        setVoucherError(err.message || "Failed to load vouchers");
        console.error("Error fetching vouchers:", err);
        setVouchers([]);
      } finally {
        setLoadingVouchers(false);
      }
    };

    fetchVouchers();
  }, [token, voucherFilters]);

  // ─── HANDLE VOUCHER PURCHASE ───
  const handleVoucherPurchase = async (voucherId) => {
    if (!token) {
      setErrorMessage("Please log in to purchase vouchers");
      setShowErrorToast(true);
      return;
    }

    try {
      setPurchasingVoucherId(voucherId);
      console.log(`🛒 Attempting to purchase voucher: ${voucherId}`);

      const response = await fetch(
        `${API_BASE_URL}/vouchers/${voucherId}/redeem`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to purchase voucher");
      }

      const xpGained = 50;
      setUserXP((prev) => prev + xpGained);
      setShowAchievementPopup(true);
      setTimeout(() => setShowAchievementPopup(false), 3000);

      setSuccessMessage(
        `🎉 Voucher purchased! ₹${data.voucher?.denomination} added! +${xpGained} XP earned!`,
      );
      setShowSuccessToast(true);
      setShowVoucherModal(false);

      setVoucherFilters({ ...voucherFilters });

      console.log("✅ Voucher purchased:", data);
    } catch (err) {
      setErrorMessage(err.message || "Failed to purchase voucher");
      setShowErrorToast(true);
      console.error("Error purchasing voucher:", err);
    } finally {
      setPurchasingVoucherId(null);
    }
  };

  // ─── HANDLE VOUCHER FILTER CHANGE ───
  const handleVoucherFilterChange = (filterType, value) => {
    setVoucherFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  // ─── FILTER GAME ACCOUNTS ───
  const filteredAccounts = gameAccounts.filter(
    (account) =>
      account.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.game.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // ─── SORT GAMES ───
  const sortedGames = [...games].sort((a, b) => {
    if (sortBy === "popular") return b.popularity - a.popularity;
    if (sortBy === "newest")
      return new Date(b.createdAt) - new Date(a.createdAt);
    if (sortBy === "price-low")
      return a.packs?.[0]?.retailPrice - b.packs?.[0]?.retailPrice;
    if (sortBy === "price-high")
      return b.packs?.[0]?.retailPrice - a.packs?.[0]?.retailPrice;
    return 0;
  });

  // ─── LOADING STATE ───
  if (loading && loadingAccounts) {
    return (
      <Container className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading marketplace...</p>
      </Container>
    );
  }

  return (
    <div className={`${styles.marketplace} ${styles[theme]}`}>
      {/* TOAST NOTIFICATIONS */}
      <ToastContainer
        position="top-end"
        className="p-3"
        style={{ zIndex: 9999 }}
      >
        <Toast
          show={showSuccessToast}
          onClose={() => setShowSuccessToast(false)}
          delay={5000}
          autohide
          bg="success"
        >
          <Toast.Header closeButton>
            <strong className="me-auto">✅ Success</strong>
          </Toast.Header>
          <Toast.Body className="text-white">{successMessage}</Toast.Body>
        </Toast>

        <Toast
          show={showErrorToast}
          onClose={() => setShowErrorToast(false)}
          delay={5000}
          autohide
          bg="danger"
        >
          <Toast.Header closeButton>
            <strong className="me-auto">❌ Error</strong>
          </Toast.Header>
          <Toast.Body className="text-white">{errorMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
      {/* HERO SECTION */}
      <section className={`${styles.heroSection} ${styles[theme]}`}>
        <div className={styles.heroBackground}>
          <div
            className={styles.floatingShape}
            style={{ animationDelay: "0s" }}
          ></div>
          <div
            className={styles.floatingShape}
            style={{ animationDelay: "1s" }}
          ></div>
          <div
            className={styles.floatingShape}
            style={{ animationDelay: "2s" }}
          ></div>
        </div>
        <Container className={styles.heroContainer}>
          <div className={styles.heroContent}>
            <div className={styles.heroTextWrapper}>
              <Badge bg="light" text="dark" className={styles.heroBadge}>
                <Gamepad2 className="inline" /> Welcome to PixelMoon
              </Badge>
              <h1 className={styles.heroTitle}>Epic Gaming Marketplace</h1>
              <p className={styles.heroSubtitle}>
                Complete quests, earn XP, unlock achievements, and become a
                legendary collector!
              </p>
              <div className={styles.heroStats}>
                <div className={styles.stat}>
                  <span className={styles.statNumber}>{games.length}+</span>
                  <span className={styles.statLabel}>Games</span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.statNumber}>
                    {gameAccounts.length}+
                  </span>
                  <span className={styles.statLabel}>Accounts</span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.statNumber}>{vouchers.length}+</span>
                  <span className={styles.statLabel}>Quests</span>{" "}
                  {/* Updated label */}
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>
      {/* SEARCH & FILTER SECTION */}
      <section className={styles.filterSection}>
        <Container>
          <div className={styles.filterCard}>
            <div className={styles.searchWrapper}>
              <span className={styles.searchIcon}>
                <Search />
              </span>
              <Form.Control
                type="search"
                placeholder="Search games, accounts, quests..." // Updated placeholder
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
            </div>

            <div className={styles.filterControls}>
              <Form.Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="">📂 All Categories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    📂 {category}
                  </option>
                ))}
              </Form.Select>

              <Form.Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="popular">⭐ Most Popular</option>
                <option value="newest">✨ Newest</option>
                <option value="price-low">💰 Price: Low to High</option>
                <option value="price-high">💎 Price: High to Low</option>
              </Form.Select>
            </div>
          </div>
        </Container>
      </section>
      {/* QUESTS SECTION */}
      {(loadingVouchers || vouchers.length > 0) && (
        <section className={styles.questsSection}>
          {" "}
          {/* Renamed from vouchersSection */}
          <Container>
            <div className={styles.sectionHeader}>
              <div className={styles.headerContent}>
                <h2 className={styles.sectionTitle}>
                  <span className={styles.titleIcon}>⚡</span> Daily Quests &
                  Rewards {/* Updated title */}
                </h2>
                <p className={styles.sectionSubtitle}>
                  Complete quests to earn rewards and special vouchers
                </p>{" "}
                {/* Updated subtitle */}
              </div>
              <Link to="/vouchers" className={styles.viewAllLink}>
                View All Quests → {/* Updated link text */}
              </Link>
            </div>

            {/* QUEST FILTERS */}
            <div className={styles.questFiltersContainer}>
              {" "}
              {/* Renamed from voucherFiltersContainer */}
              <Form.Select
                value={voucherFilters.type}
                onChange={(e) =>
                  handleVoucherFilterChange("type", e.target.value)
                }
                className={styles.questFilterSelect} // Renamed from voucherFilterSelect
                size="sm"
              >
                <option value="">⚔️ All Quest Types</option>{" "}
                {/* Updated option text */}
                <option value="smileone">😊 SmileOne Quests</option>{" "}
                {/* Updated option text */}
                <option value="moo">🐄 Moo Quests</option>{" "}
                {/* Updated option text */}
                <option value="custom">⭐ Epic Quests</option>{" "}
                {/* Updated option text */}
              </Form.Select>
              <Form.Select
                value={voucherFilters.status}
                onChange={(e) =>
                  handleVoucherFilterChange("status", e.target.value)
                }
                className={styles.questFilterSelect} // Renamed from voucherFilterSelect
                size="sm"
              >
                <option value="active">✅ Active</option>
                <option value="redeemed">✔️ Completed</option>{" "}
                {/* Updated option text */}
                <option value="expired">⏰ Expired</option>
              </Form.Select>
            </div>

            {voucherError && (
              <Alert
                variant="warning"
                dismissible
                onClose={() => setVoucherError("")}
              >
                {voucherError}
              </Alert>
            )}

            {loadingVouchers ? (
              <div className={styles.loadingContainer}>
                <Spinner animation="border" role="status" />
                <p className="mt-2">Loading quests...</p>{" "}
                {/* Updated loading text */}
              </div>
            ) : vouchers.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>📦</div>
                <h4>No quests available</h4> {/* Updated empty state text */}
                <p>Check back soon for new challenges!</p>{" "}
                {/* Updated empty state text */}
              </div>
            ) : (
              <Row xs={1} md={2} lg={3} xl={4} className="g-4">
                {vouchers.slice(0, 4).map((voucher) => (
                  <Col key={voucher._id}>
                    <div
                      className={styles.questCard} // Renamed from voucherCard
                      onClick={() => {
                        setSelectedVoucher(voucher);
                        setShowVoucherModal(true);
                      }}
                    >
                      <div className={styles.questImage}>
                        {" "}
                        {/* Renamed from voucherImage */}
                        <div className={styles.questIcon}>
                          {" "}
                          {/* Renamed from voucherIcon */}
                          {voucher.type === "smileone" && "😊"}
                          {voucher.type === "moo" && "🐄"}
                          {voucher.type === "custom" && "⭐"}
                        </div>
                        <Badge bg="danger" className={styles.questBadge}>
                          {" "}
                          {/* Renamed from voucherBadge */}⚡ Limited{" "}
                          {/* Updated badge text */}
                        </Badge>
                      </div>
                      <div className={styles.questContent}>
                        {" "}
                        {/* Renamed from voucherContent */}
                        <h5 className={styles.questName}>
                          {voucher.name}
                        </h5>{" "}
                        {/* Renamed from voucherName */}
                        <div className={styles.questDetails}>
                          {" "}
                          {/* Renamed from voucherDetails */}
                          <span className={styles.questReward}>
                            ₹{voucher.denomination}
                          </span>{" "}
                          {/* Renamed from denomination */}
                          <span className={styles.questStock}>
                            {voucher.maxUses - voucher.usageCount} left
                          </span>{" "}
                          {/* Renamed from available */}
                        </div>
                        <div className={styles.questProgress}>
                          {" "}
                          {/* Added quest progress bar */}
                          <ProgressBar
                            now={Math.random() * 100}
                            className={styles.miniProgressBar}
                          />
                        </div>
                        <div className={styles.questFooter}>
                          {" "}
                          {/* Renamed from voucherFooter */}
                          <div className={styles.priceGroup}>
                            <span className={styles.questPrice}>
                              ₹{voucher.price}
                            </span>{" "}
                            {/* Renamed from currentPrice */}
                          </div>
                          <Button
                            size="sm"
                            className={styles.claimBtn} // Renamed from buyBtn
                            onClick={(e) => {
                              e.stopPropagation();
                              handleVoucherPurchase(voucher._id);
                            }}
                            disabled={purchasingVoucherId === voucher._id}
                          >
                            {purchasingVoucherId === voucher._id ? (
                              <>
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-hidden="true"
                                  className="me-2"
                                />
                                Claiming... {/* Updated button text */}
                              </>
                            ) : (
                              "Claim ⚡" // Updated button text
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            )}
          </Container>
        </section>
      )}
      {/* FEATURED GAMES SECTION */}
      <section className={styles.gamesSection}>
        <Container>
          <div className={styles.sectionHeader}>
            <div className={styles.headerContent}>
              <h2 className={styles.sectionTitle}>
                <span className={styles.titleIcon}>🎮</span> Featured Games
                Collection {/* Updated title */}
              </h2>
              <p className={styles.sectionSubtitle}>
                Explore {games.length} legendary games to add to your collection
              </p>{" "}
              {/* Updated subtitle */}
            </div>
          </div>

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {games.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>😓</div>
              <h4>No games found</h4>
              <p>Try adjusting your filters or search terms</p>
            </div>
          ) : (
            <Row xs={1} md={2} lg={3} xl={4} className="g-4">
              {sortedGames.map((game) => (
                <Col key={game._id}>
                  <Link
                    to={`/games/${game._id}`}
                    className="text-decoration-none"
                  >
                    <Card className={styles.gameCard}>
                      <div className={styles.imageWrapper}>
                        <Card.Img
                          variant="top"
                          src={game.image || "/images/default-game.jpg"}
                          alt={game.name}
                          className={styles.gameImage}
                        />
                        <div className={styles.imageOverlay}>
                          <Button className={styles.playBtn}>Explore →</Button>{" "}
                          {/* Updated button text */}
                        </div>
                        <div className={styles.badgeGroup}>
                          <Badge bg="success" className={styles.activeBadge}>
                            ✓ Active
                          </Badge>
                          {game.featured && (
                            <Badge
                              bg="warning"
                              className={styles.featuredBadge}
                            >
                              🔥 Featured {/* Updated badge text */}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Card.Body className={styles.cardBody}>
                        <Card.Title className={styles.gameTitle}>
                          {game.name}
                        </Card.Title>
                        <Card.Text className={styles.gameDescription}>
                          {game.description?.substring(0, 60)}...
                        </Card.Text>
                        <div className={styles.gameFooter}>
                          <div className={styles.gameInfo}>
                            <Badge
                              bg="primary"
                              className={styles.categoryBadge}
                            >
                              {game.category || "Game"}
                            </Badge>
                            <span className={styles.region}>
                              {game.region === "Global" ? "🌍" : "🌐"}{" "}
                              {game.region || "Global"}
                            </span>
                          </div>
                          <div className={styles.rating}>
                            {"⭐".repeat(
                              Math.min(5, Math.floor(game.rating || 4.5)),
                            )}
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Link>
                </Col>
              ))}
            </Row>
          )}
        </Container>
      </section>
      {/* PREMIUM ACCOUNTS SECTION */}
      <section className={styles.accountsSection}>
        <Container>
          <div className={styles.sectionHeader}>
            <div className={styles.headerContent}>
              <h2 className={styles.sectionTitle}>
                <span className={styles.titleIcon}>👑</span> Premium Accounts &
                Items {/* Updated title */}
              </h2>
              <p className={styles.sectionSubtitle}>
                Unlock {filteredAccounts.length} premium accounts to level up
                your gaming
              </p>{" "}
              {/* Updated subtitle */}
            </div>
          </div>

          {errorAccounts && (
            <Alert
              variant="warning"
              dismissible
              onClose={() => setErrorAccounts("")}
            >
              {errorAccounts}
            </Alert>
          )}

          {loadingAccounts ? (
            <div className={styles.loadingContainer}>
              <Spinner animation="border" role="status" />
            </div>
          ) : filteredAccounts.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>😓</div>
              <h4>No accounts found</h4>
              <p>Check back soon for more legendary listings</p>{" "}
              {/* Updated empty state text */}
            </div>
          ) : (
            <Row xs={1} md={2} lg={3} xl={4} className="g-4">
              {filteredAccounts.map((account) => (
                <Col key={account._id}>
                  <Link
                    to={`/accounts/${account._id}`}
                    className="text-decoration-none"
                  >
                    <Card className={styles.accountCard}>
                      {account.images && account.images.length > 0 && (
                        <div className={styles.accountImageWrapper}>
                          <Card.Img
                            variant="top"
                            src={account.images[0]}
                            alt={account.title}
                            className={styles.accountImage}
                          />
                          <div className={styles.accountOverlay}>
                            <Button className={styles.viewBtn}>
                              Inspect →
                            </Button>{" "}
                            {/* Updated button text */}
                          </div>
                          <Badge bg="info" className={styles.accountBadge}>
                            {account.status || "Available"}
                          </Badge>
                        </div>
                      )}
                      <Card.Body className={styles.accountCardBody}>
                        <Card.Title className={styles.accountTitle}>
                          {account.title}
                        </Card.Title>
                        <div className={styles.accountMeta}>
                          <span className={styles.game}>🎮 {account.game}</span>
                          {account.category && (
                            <span className={styles.category}>
                              📁 {account.category}
                            </span>
                          )}
                        </div>
                        <Card.Text className={styles.accountDescription}>
                          {account.description?.substring(0, 60)}...
                        </Card.Text>
                        <div className={styles.accountFooter}>
                          <div className={styles.priceSection}>
                            <span className={styles.priceLabel}>PRICE</span>
                            <span className={styles.price}>
                              ₹{account.baseInrCost}
                            </span>
                          </div>
                          <Button size="sm" className={styles.addToCartBtn}>
                            🛒 Collect {/* Updated button text */}
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Link>
                </Col>
              ))}
            </Row>
          )}
        </Container>
      </section>
      {/* LEGENDARY REWARDS BANNER */}
      <section className={styles.rewardSection}>
        <Container>
          <div className={`${styles.rewardBanner} ${styles[theme]}`}>
            <div className={styles.rewardContent}>
              <h3 className={styles.rewardTitle}>🏆 Legendary Rewards Await</h3>
              <p className={styles.rewardText}>
                Reach Gold tier to unlock exclusive discounts and epic rewards
              </p>
              <Button size="lg" className={styles.rewardBtn}>
                View Your Rewards →
              </Button>
            </div>
            <div className={styles.rewardVisual}>
              <div className={styles.rewardShape}></div>
            </div>
          </div>
        </Container>
      </section>
      {/* QUEST DETAIL MODAL */}
      <Modal
        show={showVoucherModal}
        onHide={() => setShowVoucherModal(false)}
        centered
        className={styles.questModal}
      >
        {" "}
        {/* Renamed from voucherModal */}
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title>⚡ Quest Details</Modal.Title> {/* Updated title */}
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          {selectedVoucher && (
            <>
              <div className={styles.modalQuestIcon}>
                {" "}
                {/* Renamed from modalVoucherIcon */}
                {selectedVoucher.type === "smileone" && "😊"}
                {selectedVoucher.type === "moo" && "🐄"}
                {selectedVoucher.type === "custom" && "⭐"}
              </div>
              <h4 className={styles.modalQuestName}>{selectedVoucher.name}</h4>{" "}
              {/* Renamed from modalVoucherName */}
              <p className={styles.modalQuestDesc}>
                {selectedVoucher.description}
              </p>{" "}
              {/* Renamed from modalVoucherDesc */}
              <div className={styles.modalInfoGrid}>
                <div className={styles.infoCard}>
                  <span className={styles.infoLabel}>Reward Value</span>{" "}
                  {/* Updated label */}
                  <span className={styles.infoValue}>
                    ₹{selectedVoucher.denomination}
                  </span>
                </div>
                <div className={styles.infoCard}>
                  <span className={styles.infoLabel}>Quest Cost</span>{" "}
                  {/* Updated label */}
                  <span className={styles.infoValue}>
                    ₹{selectedVoucher.price}
                  </span>
                </div>
                <div className={styles.infoCard}>
                  <span className={styles.infoLabel}>Remaining</span>{" "}
                  {/* Updated label */}
                  <span className={styles.infoValue}>
                    {selectedVoucher.maxUses - selectedVoucher.usageCount}
                  </span>
                </div>
                <div className={styles.infoCard}>
                  <span className={styles.infoLabel}>Quest Type</span>{" "}
                  {/* Updated label */}
                  <span className={styles.infoValue}>
                    {selectedVoucher.type}
                  </span>
                </div>
              </div>
              {/* EXPIRY WARNING */}
              {selectedVoucher.expiresAt && (
                <div className={styles.expiryWarning}>
                  <small>
                    ⏰ Expires on:{" "}
                    {new Date(selectedVoucher.expiresAt).toLocaleDateString()}
                  </small>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <Button
            variant="outline-secondary"
            onClick={() => setShowVoucherModal(false)}
          >
            Close
          </Button>
          <Button
            className={styles.claimNowBtn} // Renamed from buyNowBtn
            onClick={() => {
              if (selectedVoucher?._id) {
                handleVoucherPurchase(selectedVoucher._id);
              } else {
                setErrorMessage(
                  "Quest information is missing. Please try again.",
                ); // Updated error message
                setShowErrorToast(true);
              }
            }}
            disabled={
              purchasingVoucherId === selectedVoucher?._id || !selectedVoucher
            }
          >
            {purchasingVoucherId === selectedVoucher?._id ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Claiming... {/* Updated button text */}
              </>
            ) : (
              `⚡ Claim Quest - ₹${selectedVoucher?.price || "0"}` // Updated button text
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GameList;
